import { QK } from '@api/queryKeys';
import { companyKeys } from '@shared/api/query-keys';
import {
  CreateCompanyDto,
  DeleteCompanyResponse,
  GetCompanyParams,
  LegalFormId,
} from '@api/types/company';
import { UpdateTeamMemberPayload, UpdateTeamMemberSignatoryPayload } from '@api/types/team';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { companyApi } from './company';
import { companyQueries } from './queries';

export const useCompany = (id?: string | number, params?: GetCompanyParams, enabled?: boolean) =>
  useQuery({
    ...companyQueries.detail(id, params),
    enabled: Boolean(id) && enabled,
  });

export const useCreateCompanyMutation = () => {
  return useMutation({
    mutationFn: (body: CreateCompanyDto) => companyApi.createCompany(body),
  });
};

export const useUpdateCompanyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreateCompanyDto }) =>
      companyApi.updateCompany(id, data),
    onSuccess: async (_result, { id }) => {
      await queryClient.invalidateQueries({ queryKey: QK.audits() });
      await queryClient.invalidateQueries({ queryKey: companyKeys.detail(id) });
    },
  });
};

export const useLegalForms = (enabled = true) =>
  useQuery({
    ...companyQueries.legalForms(),
    enabled,
    meta: { showErrorToast: true },
  });

/**
 * A legal-form pick. Every outcome re-reads the company: a 409 means a register answer or an
 * override got there first, so the field must lock. A refusal toasts the server's sentence; the
 * 403 stays the interceptor's (logout), so call this only where `legalFormEditable` is true.
 *
 * That flag rides a company read up to 5 minutes old, so the pick asks again first and posts
 * nothing (resolving null) once the server no longer lets this caller set the form; the re-read
 * then locks the field. The check reads `GET /company/:id` directly rather than through the
 * cache: `detail` keys on the id alone, and a param-less read would replace the page's own.
 */
export const useSetCompanyLegalFormMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, legalForm }: { id: number; legalForm: LegalFormId }) => {
      const { company } = await companyApi.getCompany(id);
      if (company.legalFormEditable !== true) return null;
      return companyApi.setLegalForm(id, { legalForm });
    },
    onSettled: async (_result, _error, { id }) => {
      await queryClient.invalidateQueries({ queryKey: companyKeys.detail(id) });
    },
    meta: { showErrorToast: true },
  });
};

export const useDeleteCompany = () => {
  return useMutation<DeleteCompanyResponse, Error, { id: string | number }>({
    mutationFn: ({ id }) => companyApi.deleteCompany(id),
  });
};

export const useGetCommercialRegisterURL = (id?: string | number, enabled = true) => {
  return useQuery({
    ...companyQueries.commercialRegister(id),
    enabled: Boolean(id) && enabled,
    retry: false,
  });
};

export const useInviteMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, invite }: { id: number; invite: { email: string; role: string } }) =>
      companyApi.inviteMember(id, invite),
    onSuccess: async (_result, { id }) => {
      await queryClient.invalidateQueries({ queryKey: companyKeys.detail(id) });
    },
  });
};

export const useChangeMemberRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      memberId,
      body,
    }: {
      id: number;
      memberId: number;
      body: UpdateTeamMemberPayload;
    }) => companyApi.changeMemberRole(id, memberId, body),
    onSuccess: async (_result, { id }) => {
      await queryClient.invalidateQueries({ queryKey: companyKeys.detail(id) });
    },
  });
};

export const useChangeMemberSignatory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      memberId,
      body,
    }: {
      id: number;
      memberId: number;
      body: UpdateTeamMemberSignatoryPayload;
    }) => companyApi.changeMemberSignatory(id, memberId, body),
    onSuccess: async (_result, { id }) => {
      await queryClient.invalidateQueries({ queryKey: companyKeys.detail(id) });
    },
  });
};

export const useRemoveMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, memberId }: { id: number; memberId: number }) =>
      companyApi.removeMember(id, memberId),
    onSuccess: async (_result, { id }) => {
      await queryClient.invalidateQueries({ queryKey: companyKeys.detail(id) });
    },
  });
};

export const useZefixCompanies = (query: string, options: { enabled?: boolean } = {}) => {
  return useQuery({
    ...companyQueries.zefix(query),
    enabled: Boolean(query) && options.enabled,
    retry: false,
  });
};
